<template>
  <main-section
    :isEditMode="isEditMode"
    :isDiff="group.isDiff"
    :isOutdated="isOutdated"
    :itemType="'GROUP'"
    :typename="'group'"
    :item="group"
    :onUpdate="updateGroup"
    :onCreate="createGroup"
    :onDelete="deleteGroup"
    @change-chosen="onChangeChosen"
    @revert="onRevert"
    :isEdited="isGroupEdited"
  >
    <fieldset slot="fieldset" :disabled="isOutdated">
      <div
        class="field required"
        v-diff-field.disable="groupDiff.name"
        :class="{ error: $v.group.name.$error || groupUnfulfilledUniqueConstraints['name'] }"
      >
        <label for="groupName">{{ $t('NAME') }}</label>
        <input
          id="groupName"
          type="text"
          :placeholder="$t('NAME')"
          @input="groupUnfulfilledUniqueConstraints['name'] = false"
          v-model.trim="group.name"
          @click="$v.group.name.$touch()"
        />
      </div>
      <div class="two fields">
        <div class="twelve wide field" v-diff-field.disable="groupDiff.description">
          <label for="groupDescription">{{ $t('DESCRIPTION') }}</label>
          <input
            id="groupDescription"
            type="text"
            :placeholder="$t('DESCRIPTION')"
            v-model.trim="group.description"
          />
        </div>
        <div class="four wide field" v-diff-field.disable="groupDiff.key">
          <label for="groupKey">{{ $t('KEY') }}</label>
          <input id="groupKey" type="text" :placeholder="$t('KEY')" v-model.trim="group.key" />
        </div>
      </div>
      <div class="field" v-diff-field.disable="groupDiff.color">
        <label for="groupColor">{{ $t('COLOR') }}</label>
        <div class="color-buttons">
          <input id="groupColor" type="color" v-model="group.color" />
          <div
            class="ui mini labeled icon button"
            :disabled="group.isDiff"
            @click.prevent="resetColor"
          >
            {{ $t('RESET_COLOR') }}
            <i class="undo icon"></i>
          </div>
        </div>
      </div>

      <!-- TODO add group selector -->

      <div class="field">
        <group-typeahead
          :description="$t('GROUP_INCLUDED_IN_GROUP_DESCRIPTION')"
          :allGroups="groups"
          :currentGroups="editedGroups"
          @selected="groupSelected"
          @created="groupCreated"
          @removed="groupRemoved"
          :disabled="group.isDiff || !groups"
        />
      </div>

      <h4 class="ui horizontal left aligned divider header">{{ $t('GROUP_GENERAL_SETTINGS') }}</h4>
      <div class="ui basic segment">
        <div class=" fields">
          <div class="six wide field" v-diff-field.disable="groupDiff.promptOnAdd">
            <div class="ui toggle checkbox">
              <input
                id="promptOnAdd"
                type="checkbox"
                v-model="group.promptOnAdd"
                @change="changePromptOnAdd"
              />
              <label for="promptOnAdd">
                {{ $t('PROMPT_ON_ADD') }}
              </label>
            </div>
          </div>
          <div class="ten wide field" v-diff-field="groupDiff.promptOnAdd">
            <label for="promptOnAddMsg">{{ $t('PROMPT_ON_ADD_MESSAGE') }}</label>
            <input
              id="promptOnAddMsg"
              type="text"
              :placeholder="$t('ADD_ITEM_CONFIRM', { thing: group.name })"
              v-model="group.promptOnAddMsg"
              :disabled="!group.promptOnAdd || group.isDiff"
            />
          </div>
        </div>
        <div class="fields">
          <div class="six wide field" v-diff-field.disable="groupDiff.promptOnRemove">
            <div class="ui toggle checkbox">
              <input
                id="promptOnRemove"
                type="checkbox"
                v-model="group.promptOnRemove"
                @change="changePromptOnRemove"
              />
              <label for="promptOnRemove">
                {{ $t('PROMPT_ON_REMOVE') }}
              </label>
            </div>
          </div>
          <div class="ten wide field" v-diff-field="groupDiff.promptOnRemoveMsg">
            <label for="promptOnRemoveMsg">{{ $t('PROMPT_ON_REMOVE_MESSAGE') }}</label>
            <input
              id="promptOnRemoveMsg"
              type="text"
              :placeholder="$t('REMOVE_ITEM_CONFIRM', { thing: group.name })"
              v-model="group.promptOnRemoveMsg"
              :disabled="!group.promptOnRemove || group.isDiff"
            />
          </div>
        </div>
      </div>

      <h4 class="ui horizontal left aligned divider header">{{ $t('GROUP_FLOW_SETTINGS') }}</h4>
      <div class="ui basic segment">
        <div class="field" v-diff-field.disable="groupDiff.disableAutosave">
          <div class="ui toggle checkbox">
            <input id="disableAutosave" type="checkbox" v-model="group.disableAutosave" />
            <label for="disableAutosave">
              {{ $t('DISABLE_AUTOSAVE') }}
            </label>
          </div>
        </div>
        <div class="field" v-diff-field.disable="groupDiff.disableFlowsEditing">
          <div class="ui toggle checkbox">
            <input id="disableFlowsEditing" type="checkbox" v-model="group.disableFlowsEditing" />
            <label for="disableFlowsEditing">
              {{ $t('DISABLE_FLOW_CODE_EDITING') }}
            </label>
          </div>
        </div>
      </div>

      <div class="ui four column doubling grid" style="margin-top: 3em;">
        <div class="column">
          <add-delete-list
            :icon="'user'"
            :type="'USER'"
            :allItems="allUsers"
            :currentItems="currentGroup.user"
            :to="
              u => {
                return { name: 'user', params: { userId: u.id } }
              }
            "
            @remove="removeAffiliate('user', $event)"
            @add="addAffiliate('user', $event)"
            :diff="groupContainsDiff.user"
            :isDisabled="group.isDiff"
          />
        </div>
        <div class="column">
          <add-delete-list
            :icon="'cube'"
            :type="'APPLICATION'"
            :allItems="appsWithIdentifyingNames"
            :currentItems="currentGroup.application"
            :to="
              a => {
                return { name: 'app-settings', params: { appId: a.id } }
              }
            "
            @remove="removeAffiliate('application', $event)"
            @add="addAffiliate('application', $event)"
            :diff="groupContainsDiff.app"
            :isDisabled="group.isDiff"
          />
        </div>
        <div class="column">
          <add-delete-list
            :icon="'random'"
            :type="'FLOW'"
            :allItems="flowsWithIdentifyingNames"
            :currentItems="currentGroupFlowsWithWarnings"
            :to="
              f => {
                return {
                  name: 'app-flow',
                  params: { appId: f.application, flowId: f.id, mode: 'auto' }
                }
              }
            "
            @remove="removeAffiliate('flow', $event)"
            @add="addAffiliate('flow', $event)"
            :diff="groupContainsDiff.flow"
            :isDisabled="group.isDiff"
          />
        </div>
        <div class="column">
          <add-delete-list
            :icon="'group'"
            :type="'GROUP'"
            :allItems="allGroups"
            :currentItems="currentGroup.group"
            :to="
              g => {
                return {
                  name: 'group',
                  params: { groupId: g.id }
                }
              }
            "
            @remove="removeAffiliate('group', $event)"
            @add="addAffiliate('group', $event)"
            :diff="groupContainsDiff.group"
            :isDisabled="group.isDiff"
          />
        </div>
      </div>
    </fieldset>

    <loading-button
      slot="rightBottomMenuSlot2"
      class="ui labeled icon button"
      :onClick="copyGroup"
      v-if="isEditMode"
      :disabled="isOutdated || group.isDiff"
    >
      {{ $t('COPY', { thing: '' }) }}
      <i class="copy icon"></i>
    </loading-button>
  </main-section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';

.checkbox {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.color-buttons {
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 5px;
}
</style>

<script>
// @flow
import Vue from 'vue'
import { AddDeleteList, LoadingButton, MainSection, GroupTypeahead } from 'ui'
import {
  Group,
  Toast,
  User,
  Application,
  Flow,
  Typenames,
  ErrorCodes,
  UnsavedChecker
} from 'services'
import { required } from 'vuelidate/lib/validators'
import { map } from 'rxjs/operators'
import _ from 'lodash'
import { ActionTypes } from 'store'
import GroupAffiliated from '../Mixins/GroupAffiliated'

const group = {
  ...Group.defaultDescriptive,
  name: ''
}

const affiliateTypes = ['user', 'application', 'flow', 'group']

const defaultAffiliates = () =>
  _.reduce(
    affiliateTypes,
    (memo, type) => {
      memo[type] = []
      return memo
    },
    {}
  )

const defaultDiff = () =>
  _.reduce(
    affiliateTypes,
    (memo, type) => {
      memo[type] = {}
      return memo
    },
    {}
  )

export default {
  name: 'group',
  mixins: [UnsavedChecker.mixin, GroupAffiliated],
  components: { LoadingButton, AddDeleteList, MainSection, GroupTypeahead },
  data() {
    return {
      mode: '',
      group: { ...group },
      isOutdated: false,
      removed: defaultAffiliates(),
      added: defaultAffiliates(),
      groupDiff: {},
      groupContainsDiff: defaultDiff(),
      groupUnfulfilledUniqueConstraints: { name: false }
    }
  },

  computed: {
    allApps() {
      return this.$store.state.application.items
    },
    allUsers() {
      return this.$store.state.user.items
    },
    allFlows() {
      return this.$store.state.flow.items
    },
    allGroups() {
      return this.$store.state.group.items
    },
    savedGroups() {
      return _.filter(this.groups, g => _.includes(g.group, this.group.id))
    },

    all() {
      return {
        user: this.allUsers,
        application: this.allApps,
        flow: this.allFlows,
        group: this.allGroups
      }
    },

    isGroupEdited() {
      const isEdited = type => {
        return this.removed[type]?.length > 0 || this.added[type]?.length > 0
      }
      return (
        _.reduce(affiliateTypes, (memo, type) => memo || isEdited(type), false) ||
        this.areGroupsEdited
      )
    },
    isEditMode() {
      return this.mode === 'edit'
    },
    currentGroup() {
      const currentOf = type => {
        // The current items are the ones in group that are not in removed + added of the type
        return _.concat(_.differenceWith(this.group[type], this.removed[type]), this.added[type])
      }

      const fromDiff = type => {
        return _.concat(this.groupContainsDiff[type].added, this.groupContainsDiff[type].removed)
      }

      const inflate = (type, ids) => {
        return _.map(ids, id => _.find(this.all[type], item => item.id === id))
      }

      const produce = type =>
        _.filter(inflate(type, _.uniq(_.concat(currentOf(type), fromDiff(type)))))

      return _.reduce(
        affiliateTypes,
        (memo, type) => {
          memo[type] = produce(type)
          return memo
        },
        {}
      )
    },
    currentGroupFlowsWithWarnings() {
      return this.currentGroup.flow.map(f => {
        let app = _.find(this.allApps, { id: f.application })
        let isFlowAppInAGroup = !_.isEmpty(
          _.find(this.currentGroup.application, { id: app && app.id })
        )
        return {
          ...f,
          isWarning: !isFlowAppInAGroup,
          warning: 'FLOWS_APPLICATION_WARNING'
        }
      })
    },
    appsWithIdentifyingNames() {
      return this.allApps.map(a => {
        return {
          ...a,
          name: this.$i18n.t('IDENTIFYING_APP_NAME', {
            appName: a.name,
            appVersion: a.appVersion
          })
        }
      })
    },
    flowsWithIdentifyingNames() {
      return this.allFlows.map(f => {
        let app = _.find(this.allApps, { id: f.application })
        return app && app.name
          ? {
              ...f,
              name: this.$i18n.t('IDENTIFYING_FLOW_NAME', {
                appName: app.name,
                flowName: f.name,
                appVersion: app.appVersion
              })
            }
          : f
      })
    }
  },

  validations: {
    group: {
      name: {
        required
      }
    }
  },

  methods: {
    readGroup(groupId: string) {
      return Group.readGroup(groupId)
        .then(group => {
          this.group = group
          this.clearAddedRemovedItems()
          this.isOutdated = false
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('READ', { thing: this.$i18n.tc('GROUP', 1) })
            })
          )
          console.error(err)
        })
    },

    createGroup() {
      if (!this.$v.$invalid) {
        this.updateGroupUsersApplicationsAndFlows()
        return Group.createGroup(this.group)
          .then(res => {
            Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('GROUP', 1) }), {
              remove: 5000
            })
            this.$router.push({ name: 'group', params: { groupId: res.id } })
          })
          .catch(err => {
            if (err.code === ErrorCodes.ITEM_ALREADY_EXISTS) {
              this.setGroupUnfulfilledUniqueConstraints(err.constraints)
              Toast.error(
                this.$i18n.t('ERR_NOT_UNIQUE', {
                  action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('GROUP', 1) }),
                  thing: this.$i18n.tc('GROUP', 1),
                  property: err.constraints.join(', ')
                })
              )
            } else {
              Toast.error(
                this.$i18n.t('ERR', {
                  action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('GROUP', 1) })
                })
              )
              console.error(err)
            }
            throw err
          })
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('GROUP', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },
    updateGroupUsersApplicationsAndFlows() {
      _.each(affiliateTypes, type => {
        this.group[type] = _.concat(
          _.filter(this.group[type], r => !_.includes(this.removed[type], r)),
          this.added[type]
        )
      })
    },

    updateGroup() {
      if (!this.$v.$invalid) {
        if (!this.isOutdated) {
          this.updateGroupUsersApplicationsAndFlows()
          return Group.updateGroup(this.group.id, this.group)
            .then(res => {
              this.group.version = res.version

              this.removed = defaultAffiliates()
              this.added = defaultAffiliates()

              this.clearGroupUnfulfilledUniqueConstraints()
              Toast.success(this.$i18n.t('UPDATED', { thing: this.$i18n.tc('GROUP', 1) }), {
                remove: 5000
              })

              // Update groups
              return this.saveGroups('group', this.group.id)
            })
            .catch(err => {
              if (err.code === ErrorCodes.ITEM_ALREADY_EXISTS) {
                this.setGroupUnfulfilledUniqueConstraints(err.constraints)
                Toast.error(
                  this.$i18n.t('ERR_NOT_UNIQUE', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('GROUP', 1) }),
                    thing: this.$i18n.tc('GROUP', 1),
                    property: err.constraints.join(', ')
                  })
                )
              } else if (err.code === ErrorCodes.ITEM_OUTDATED) {
                this.isOutdated = true
                Toast.error(
                  this.$i18n.t('ERR_OUTDATED_VERSION', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('GROUP', 1) }),
                    thing: this.$i18n.tc('GROUP', 1)
                  })
                )
              } else {
                Toast.error(
                  this.$i18n.t('ERR', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('GROUP', 1) })
                  })
                )
                console.error(err)
              }
              throw err
            })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('GROUP', 1) }),
              thing: this.$i18n.tc('GROUP', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('GROUP', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    deleteGroup() {
      return Group.deleteGroup(this.group.id)
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('GROUP', 1) }), {
            remove: 5000
          })
          this.$router.push({ name: 'groups' })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('DELETE', { thing: this.$i18n.tc('GROUP', 1) })
            })
          )
          console.error(err)
        })
    },

    copyGroup() {
      return Group.copyGroup(
        this.group.id,
        ` ${this.$i18n.t('COPY', { thing: '' }).replace(' ', '')}`
      )
        .then(g => {
          Toast.success(this.$i18n.t('COPIED', { thing: this.$i18n.tc('GROUP', 1) }), {
            remove: 5000
          })
          this.$router.push({ name: 'group', params: { groupId: g.id } })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('COPY', { thing: this.$i18n.tc('GROUP', 1) })
            })
          )
          console.error(err)
        })
    },

    resetColor() {
      this.group.color = Group.defaultColor
    },

    changePromptOnAdd() {
      if (!this.group.promptOnAdd) {
        this.group.promptOnAddMsg = ''
      }
    },

    changePromptOnRemove() {
      if (!this.group.promptOnRemove) {
        this.group.promptOnRemoveMsg = ''
      }
    },

    editMode() {
      this.mode = 'edit'
      this.readGroup(this.$route.params.groupId)
    },

    createMode() {
      this.mode = 'create'
      this.group = { ...group }
    },

    addAffiliate(type, item) {
      const id = item.id
      if (_.includes(this.removed[type], id)) {
        this.$set(this.removed, type, _.filter(this.removed[type], r => r !== id))
      } else {
        this.$set(this.added, type, _.uniq([...this.added[type], id]))
      }
    },

    removeAffiliate(type, item) {
      const id = item.id
      if (_.includes(this.added[type], id)) {
        this.$set(this.added, type, _.filter(this.added[type], r => r !== id))
      } else {
        this.$set(this.removed, type, _.uniq([...this.removed[type], id]))
      }
    },
    onChangeChosen({ originalItem, changedItem, fullDiff, abort }) {
      if (!abort) {
        this.updateGroupContainsDiff(originalItem, changedItem)
        this.groupDiff = fullDiff
      } else {
        this.clearGroupDiff()
      }
      this.group = changedItem
    },
    updateGroupContainsDiff(originalItem, changedItem) {
      // Since these are arrays we need to seperate adds and removes.
      // An update (where .isUpdate == true) is both an add and a remove. This
      // happens when a value on the same index changed, i.e. an old thing was
      // removed and replaced with a new one.
      _.each(affiliateTypes, type => {
        this.groupContainsDiff[type].removed = _.difference(originalItem[type], changedItem[type])
        this.groupContainsDiff[type].added = _.difference(changedItem[type], originalItem[type])
      })
    },
    onRevert(revertedGroup) {
      this.group = revertedGroup
      this.clearGroupDiff()
    },
    clearGroupDiff() {
      this.groupDiff = {}
      this.groupContainsDiff = defaultDiff()
    },

    clearAddedRemovedItems() {
      this.added = defaultAffiliates()
      this.removed = defaultAffiliates()
    },

    clearGroupUnfulfilledUniqueConstraints() {
      Object.keys(this.groupUnfulfilledUniqueConstraints).forEach(k => {
        this.groupUnfulfilledUniqueConstraints[k] = false
      })
    },

    setGroupUnfulfilledUniqueConstraints(constraints) {
      constraints.forEach(c => {
        this.groupUnfulfilledUniqueConstraints[c] = true
      })
    }
  },

  created() {
    if (this.$route.params.groupId) {
      this.editMode()
    } else {
      this.createMode()
    }
    Application.readAllApps()
    User.readAllUsers()
    Flow.readAllFlows()
  },

  watch: {
    '$route.params.groupId': function(newGroupId) {
      this.$v.$reset()
      this.clearGroupUnfulfilledUniqueConstraints()
      if (newGroupId) {
        this.editMode()
      } else {
        this.createMode()
      }
    }
  }
}
</script>
